import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { StoryHttpActions, StoryMosActions } from '@common/store/story/actions/story.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { Path } from '@src/suite/common/data-models/complex-types/path';
import { EnpsField } from '@src/suite/common/data-models/fields/enps-field';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { BasicContentService } from '@src/suite/common/services/basic-content-service';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { MosEventService } from '@src/suite/common/services/mos-event.service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { StoryService } from '@src/suite/common/services/story-service';
import { FullscreenActions, OpenFullscreenModalActions } from '@src/suite/common/store/fullscreen';
import { selectStoryRecordToEdit } from '@src/suite/common/store/fullscreen/selectors/sidecar.selectors';
import { selectScriptFields } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectHomeDatabase, selectHomeProgram } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { MosActions } from '@src/suite/common/store/mos/actions/mos.actions';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { RecordHttpActions, StoriesRecordActions, TopicRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectStoryRecordByGuid, selectStoryRecordsMap } from '@src/suite/common/store/records/records.selectors';
import { selectHasLockedEditors, selectIsLockedSuccessfullyByGuid, selectIsLockingInProgressByGuid } from '@src/suite/common/store/story/selector/story.selectors';
import { ML2HtmlUtils } from '@src/suite/common/utils/ml-2-html-utils';
import { processMosNewStoryFromPlugin } from '@src/suite/common/utils/mos-utlis';
import { cleanupRecord, initializeRecord } from '@src/suite/common/utils/record-utils';
import { getDynamicStoryPropertiesData, isLockedByUser } from '@src/suite/common/utils/story-utils';
import { getTextCommandsFromText } from '@src/suite/common/utils/util';
import { PlaybookOpenAiService } from '@src/suite/playbook/services/playbook-open-ai-service';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { Duration } from 'luxon';
import { catchError, exhaustMap, filter, finalize, from, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { v4 } from 'uuid';

@Injectable()
export class StoryEffects {
    private playbookService = inject(PlaybookService);
    private nomWebServicesJson = inject(NomWebServicesJson);
    private storyService = inject(StoryService);
    private appService = inject(AppService);
    private actions$ = inject(Actions);
    private store = inject(Store);
    private playbookOpenAiService = inject(PlaybookOpenAiService);
    private basicContentService = inject(BasicContentService);
    private globalTablesService = inject(GlobalTablesService);
    private mosEventService = inject(MosEventService);
    private dialog = inject(MatDialog);

    // TODO: fix this any
    currentCommandDialogRef: MatDialogRef<any, any> = null;

    createStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryHttpActions.createNewVersionSuccess),
            exhaustMap(action => {
                const { topic } = action;
                const { Database } = topic;
                return from(this.playbookService.createNewStory(Database, topic, action?.generatedText, action.promptTitle)).pipe(
                    exhaustMap(res => {
                        if (res?.status) {
                            return from(
                                this.appService.basicContentService.updateBasicContentV2<StorytellingTopicRecord>(
                                    topic.Database,
                                    topic.Path.fullPath,
                                    topic.Guid,
                                    false,
                                    { Type: topic.Type },
                                    false,
                                    null
                                )
                            ).pipe(
                                exhaustMap(() => {
                                    // TODO: add story to record store
                                    const actions = [];
                                    actions.push(StoriesRecordActions.updateStories({ records: [res.data] }));
                                    actions.push(FullscreenActions.setLoadingProgressBar({ showLoader: false }));
                                    if (action.openNewPrompt) {
                                        actions.push(FullscreenActions.openStory({ recordGuid: res.data.Guid, recordType: ObjectTypes.SCRIPT }));
                                        return from(actions);
                                    }
                                    actions.push(FullscreenActions.openStory({ recordGuid: topic.stories[topic.stories.length - 1].Guid, recordType: ObjectTypes.SCRIPT }));
                                    return from(actions);
                                })
                            );
                        }
                    })
                );
            }),
            catchError(() => of(ApplicationActions.noOpAction()))
        );
    });

    loadPrompts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryHttpActions.loadPrompts),
            mergeMap(() => {
                return this.playbookOpenAiService.getPrompts().pipe(
                    map(data => {
                        return StoryHttpActions.loadPromptsSuccess({ data });
                    }),
                    catchError(error => {
                        return of(StoryHttpActions.loadPromptsError({ error: error }));
                    })
                );
            })
        );
    });

    generateNewVersion$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryHttpActions.generateNewVersion),
            concatLatestFrom(() => this.store.select(selectStoryRecordToEdit)),
            exhaustMap(([action, story]) => {
                const topic = action.topic;
                const promptTitle = action.prompt.title;
                return this.playbookOpenAiService.getCompletion(story.text, action.prompt.endpoint).pipe(
                    map(res => {
                        const rawText = res[action.prompt.output_primary] ?? '';
                        const parsedStoryText = `<body>${rawText.replace(/(\r\n|\n)/g, '<br/>')}</body>`;
                        if (topic?.Guid) {
                            return StoryHttpActions.createNewVersionSuccess({ topic, generatedText: parsedStoryText, promptTitle, openNewPrompt: action.openNewPrompt });
                        } else {
                            const newStory = initializeRecord<StorytellingStoryRecord>(ObjectTypes.SCRIPT);
                            this.playbookService.initializeNewStoryRecordV2(newStory, topic);
                            const { text, textCommands } = ML2HtmlUtils.fixTextCommandsFromAIPrompt(parsedStoryText);
                            if (Object.keys(textCommands).length > 0) {
                                newStory.TextCommands = textCommands;
                            }
                            newStory.text = text;
                            newStory.Title = promptTitle;
                            // topic.addStory(newStory as StoryRecord);
                            return FullscreenActions.openStory({ recordGuid: topic.stories[topic.stories.length - 1].Guid, recordType: ObjectTypes.SCRIPT });
                        }
                    })
                );
            }),
            catchError(error => {
                return of(StoryHttpActions.createNewVersionError({ error: error }));
            })
        );
    });

    createStoryTemplate$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(StoryHttpActions.createStoryTemplate),
                switchMap(action => {
                    const getStory = (): Observable<StorytellingStoryRecord> => {
                        if (action.isFullscreen) {
                            return of(action.story);
                        }
                        return this.basicContentService
                            .getBasicContent<StorytellingStoryRecord>({
                                database: action.story.Database || this.nomWebServicesJson.getDatabase(),
                                path: action.story.Path.fullPath,
                                guid: action.story.Guid,
                            })
                            .pipe(
                                map(response => {
                                    if (response.status) {
                                        return response.data;
                                    }
                                    return null;
                                })
                            );
                    };
                    return getStory().pipe(
                        switchMap(story => {
                            if (!story) {
                                return of(null);
                            }
                            const storyText = story.text || story.Text;

                            const textCommandKeys = Object.keys(story.TextCommands);
                            const textCommandValues = Object.values(story.TextCommands) || [];

                            const data = {
                                ...getDynamicStoryPropertiesData(story, this.globalTablesService),
                                CreateTime: new Date().toJSON(),
                                Type: ObjectTypes.SCRIPT,
                                Title: action.title,
                                Text: storyText,
                                TextCommandKeys: textCommandKeys,
                                TextCommandValues: textCommandValues,
                            };

                            if (story?.PSTStoryOutputType) {
                                data[PSTFieldNames.PSTStoryOutputType] = story.PSTStoryOutputType;
                            }
                            if (story?.PSTGroup) {
                                data[PSTFieldNames.PSTDesk] = story.PSTGroup;
                            }
                            if (story?.PSTStoryStatus) {
                                data[PSTFieldNames.PSTStoryStatus] = story.PSTStoryStatus;
                            }
                            if (story?.Tags) {
                                data[PSTFieldNames.PSTTags] = story.Tags;
                            }
                            if (story?.PSTTargetDate) {
                                data[PSTFieldNames.TargetPublicationDate] = story.PSTTargetDate;
                            }
                            if (story?.PSTStoryOwner) {
                                data[PSTFieldNames.PSTStoryOwner] = story.PSTStoryOwner;
                            }
                            data[PSTFieldNames.PSTPrimaryStoryGUID] = null;
                            data[PSTFieldNames.PSTSubstoriesSequence] = null;
                            let fieldsToUpdate: EnpsProperty[] = [];
                            fieldsToUpdate = this.nomWebServicesJson.convertor.toENPSFields(data);
                            fieldsToUpdate.forEach(field => {
                                field.FieldValue = this.nomWebServicesJson.convertor.serializeField(field.FieldValue, field.FieldName, ObjectTypes.SCRIPT)?.value || field.FieldValue;
                            });
                            return from(
                                this.basicContentService.createStoryTemplate({
                                    database: story.Database,
                                    path: action.path,
                                    keepApproved: false,
                                    fieldsToUpdate,
                                })
                            );
                        })
                    );
                })
            );
        },
        { dispatch: false }
    );

    lockCurrentStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryHttpActions.lockStory),
            mergeMap(({ storyGuid, editorGuid }) => {
                if (!storyGuid) {
                    return of(ApplicationActions.noOpAction());
                }
                const story = this.store.selectSignal(selectStoryRecordByGuid(storyGuid))();
                const lockState = this.store.selectSignal(selectIsLockedSuccessfullyByGuid(storyGuid))();
                const lockInProgress = this.store.selectSignal(selectIsLockingInProgressByGuid(storyGuid))();

                if (story && story.Guid && !story.Guid.startsWith('@') && !lockState && lockInProgress) {
                    return from(this.storyService.lockStory(story.Database || this.nomWebServicesJson.getDatabase(), story.Path?.fullPath, story.Guid)).pipe(
                        mergeMap(res => {
                            const actions = [];
                            if (res.status) {
                                const lockedSuccesful = res.data.Successful || res.data.UserId === this.nomWebServicesJson.UserId;
                                const lockedBy = res.data.Successful ? this.nomWebServicesJson.UserId : res.data.UserId;

                                if (lockedSuccesful) {
                                    actions.push(
                                        StoriesRecordActions.updateStories({
                                            records: [
                                                {
                                                    Guid: storyGuid,
                                                    EditLocked: true,
                                                    EditLockedBy: lockedBy,
                                                },
                                            ],
                                        })
                                    );
                                    actions.push(StoryHttpActions.lockStorySuccess({ storyGuid: storyGuid, editorGuid: editorGuid }));
                                } else {
                                    actions.push(StoryHttpActions.lockStoryError({ error: 'Failed to locked story', storyGuid: storyGuid, editorGuid: editorGuid }));
                                }
                            } else {
                                actions.push(StoryHttpActions.lockStoryError({ error: 'Failed to locked story', storyGuid: storyGuid, editorGuid: editorGuid }));
                            }
                            return actions;
                        }),
                        catchError(error => {
                            return of(StoryHttpActions.lockStoryError({ error: error, storyGuid: storyGuid, editorGuid: editorGuid }));
                        }),
                        finalize(() => {
                            return of(ApplicationActions.noOpAction());
                        })
                    );
                }

                return of(ApplicationActions.noOpAction());
            })
        );
    });

    releaseCurrentStoryLock$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryHttpActions.releaseStoryLock),
            mergeMap(({ storyGuid }) => {
                if (!storyGuid) {
                    return of(ApplicationActions.noOpAction());
                }
                const story = this.store.selectSignal(selectStoryRecordByGuid(storyGuid))();
                const hasLockedEditors = this.store.selectSignal(selectHasLockedEditors(storyGuid))();

                if (story && story.Guid && !story.Guid.startsWith('@') && isLockedByUser(story, this.nomWebServicesJson.UserId) && !hasLockedEditors) {
                    return from(this.storyService.releaseStoryLock(story.Database || this.nomWebServicesJson.getDatabase(), story.Path?.fullPath, story.Guid)).pipe(
                        mergeMap(() => {
                            return [
                                StoryHttpActions.releaseStoryLockSuccess({ storyGuid: storyGuid }),
                                StoriesRecordActions.updateStories({ records: [{ Guid: storyGuid, EditLocked: false, EditLockedBy: null }] }),
                            ];
                        }),
                        catchError(error => of(StoryHttpActions.releaseStoryLockError({ error: error, storyGuid: storyGuid })))
                    );
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    addMosItem$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(StoryMosActions.addMosItem),
                concatLatestFrom(() => [this.store.select(selectStoryRecordToEdit)]),
                map(([action, currentStory]) => {
                    if (!currentStory?.Guid || currentStory.Guid !== action.storyId) {
                        return ApplicationActions.noOpAction();
                    }
                    const commandText = action.mosStr.startsWith('[') && action.mosStr.endsWith(']') ? action.mosStr : `[${action.mosStr}]`;

                    // Emit through the service
                    this.mosEventService.emitMosItem({
                        storyGuid: currentStory.Guid,
                        mosCommand: commandText,
                    });
                })
            );
        },
        { dispatch: false }
    );

    requestStoryNew$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryMosActions.requestNewMOSStory),
            concatLatestFrom(() => [this.store.select(selectHomeDatabase), this.store.select(selectHomeProgram), this.store.select(selectScriptFields)]),
            mergeMap(([action, database, program, scriptFields]) => {
                const actions = [];

                const story = initializeRecord(ObjectTypes.SCRIPT);
                this.playbookService.initializeNewStoryRecordV2(story);
                story.Database = database;
                story.Path = new Path(`P_${program}`);
                story.Guid = v4();
                story.isNewRecord = true;
                story.isNewMOSRecord = true;

                const res = processMosNewStoryFromPlugin(action.mosStr, story, scriptFields);

                if (res.story) {
                    actions.push(PlaybookApiActions.createStory({ story: res.story, createAndNext: false }));
                }
                actions.push(
                    MosActions.mosClientCommandSend({
                        command: res.response,
                        sidecar: action.sidecar,
                        mosPluginId: action.mosPluginId,
                    })
                );
                return actions;
            }),
            catchError(error => {
                // @TODO Handle error
                console.warn(error);
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    replaceMosItem$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(StoryMosActions.replaceMosItem),
                concatLatestFrom(() => [this.store.select(selectStoryRecordToEdit)]),
                map(([action, currentStory]) => {
                    if (!action.mosStr || !action.mosItemId) return;
                    const commandText = action.mosStr.startsWith('[') && action.mosStr.endsWith(']') ? action.mosStr : `[${action.mosStr}]`;
                    this.mosEventService.emitMosItemEdit({
                        storyGuid: currentStory.Guid,
                        mosItemId: action.mosItemId,
                        mosCommand: commandText,
                    });
                })
            );
        },
        { dispatch: false }
    );

    calculateReadRateTime$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoriesRecordActions.calculateReadRateTime),
            mergeMap(action => {
                const fields: Record<string, any> = {};
                const text = action.hasSelectedText ? action?.selectedText : action?.record?.text;
                const serializedText = this.nomWebServicesJson.convertor.serializeField(text, PSTFieldNames.PSTRichText, ObjectTypes.SCRIPT);
                const textCommands = getTextCommandsFromText(text);
                fields['TextCommandKeys'] = textCommands.TextCommandKeys;
                fields['TextCommandValues'] = textCommands.TextCommandValues;
                fields[serializedText.key] = serializedText.value;

                if (action.record.isNewRecord) {
                    return of(ApplicationActions.noOpAction());
                }

                return this.basicContentService.textReadRateTime(action.record.Database, action.record.Path.fullPath, fields, action?.productionCommand).pipe(
                    filter(response => response.status),
                    exhaustMap(response => {
                        const actions = [];
                        if (action?.hasSelectedText) {
                            actions.push(StoriesRecordActions.setReadRateTime({ storyGuid: action.record.Guid, selectedReadRate: response.data }));
                        } else {
                            const updatedStory = this.store.selectSignal(selectStoryRecordsMap)().get(action.record.Guid);
                            if (updatedStory) {
                                actions.push(
                                    StoriesRecordActions.updateStories({
                                        records: [
                                            {
                                                Guid: updatedStory.Guid,
                                                readTimeInternal: Duration.fromObject({ seconds: response.data.ReadTime ?? 0 }),
                                                TextTime: Duration.fromObject({ seconds: response.data.TextTime ?? 0 }),
                                            },
                                        ],
                                    })
                                );
                            }
                        }
                        return actions;
                    }),
                    catchError(_error => {
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    createItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryHttpActions.createTopicFromStory),
            exhaustMap(action => {
                return from(this.basicContentService.storyText({ database: action.story.Database, path: action.story.Path.fullPath, guid: action.story.Guid })).pipe(
                    exhaustMap(res => {
                        const createTopicDialog = this.dialog.getDialogById('create-topic-full-screen');
                        if (res?.status) {
                            const topicToCreate = cleanupRecord(action.topic);
                            topicToCreate.Description = res.data as string;
                            // TODO: BIG ONE - after IBC we need to unify this to go through 'PlaybookApiActions.createTopic', not have duplicate logic here.
                            return from(this.playbookService.saveNewTopic(topicToCreate, [], [])).pipe(
                                exhaustMap(topicRes => {
                                    if (topicRes.status) {
                                        const fieldsToUpdate: EnpsField[] = [];
                                        fieldsToUpdate.push({ key: PSTFieldNames.PSTStoryTopicGUID, value: topicRes?.data?.Guid });
                                        const actions = [];
                                        createTopicDialog?.close();
                                        actions.push(RecordHttpActions.saveRecordChanges({ fieldsToUpdate, record: action.story }));
                                        actions.push(ListActions.addNewListGuids({ listGuids: [topicRes.data.Guid] }));
                                        actions.push(TopicRecordActions.updateTopics({ records: [topicRes.data] }));
                                        actions.push(PlaybookApiActions.createRecordDone());

                                        actions.push(OpenFullscreenModalActions.closeFullscreenModalForce());
                                        actions.push(FullscreenActions.resetCurrentNewRecord({ recordType: topicRes.data.Type }));
                                        if (action?.createAndOpen) {
                                            actions.push(
                                                OpenFullscreenModalActions.openTopicFullscreenDialog({ recordGuid: topicRes.data.Guid, recordType: topicRes.data.Type, isCreateAndNext: true })
                                            );
                                        }
                                        return from(actions);
                                    }
                                    return of(PlaybookApiActions.createRecordDone());
                                }),
                                catchError(_error => {
                                    return of(PlaybookApiActions.createRecordDone());
                                })
                            );
                        }
                        createTopicDialog?.close();
                        return of(PlaybookApiActions.createRecordDone());
                    }),
                    catchError(_error => {
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });
}
